import { useRef, useState } from "react";
import type * as React from "react";

const hasFiles = (e: React.DragEvent) => Array.from(e.dataTransfer.types).includes("Files");

/**
 * Makes an element a drop target for files from the desktop (PRTL-830, PRTL-1534).
 * Spread `dropProps` on the element and show an overlay while `isDragActive`.
 * dragenter/dragleave also fire on every child, so a counter tracks when the drag
 * has really left. Drags that carry no files (text, links) pass through untouched.
 *
 * Targets can nest (a composer inside a discussion that is itself a drop target), and
 * React drop events bubble through the component tree, portals included. The innermost
 * target takes the files; outer ones see `defaultPrevented` and only clear their overlay.
 * Nothing stops propagation: no dragleave follows a drop, so the outer targets need the
 * drop event to reset.
 */
export const useFileDrop = (onFiles: (files: File[]) => void, enabled = true) => {
    const [isDragActive, setIsDragActive] = useState(false);
    const depthRef = useRef(0);

    if (!enabled) return { isDragActive: false, dropProps: {} };

    const dropProps = {
        onDragEnter: (e: React.DragEvent) => {
            if (!hasFiles(e)) return;
            e.preventDefault();
            depthRef.current += 1;
            setIsDragActive(true);
        },
        onDragLeave: (e: React.DragEvent) => {
            if (!hasFiles(e)) return;
            e.preventDefault();
            depthRef.current = Math.max(0, depthRef.current - 1);
            if (depthRef.current === 0) setIsDragActive(false);
        },
        onDragOver: (e: React.DragEvent) => {
            if (!hasFiles(e)) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = "copy";
        },
        onDrop: (e: React.DragEvent) => {
            if (!hasFiles(e)) return;
            const handled = e.defaultPrevented;
            e.preventDefault();
            depthRef.current = 0;
            setIsDragActive(false);
            if (handled) return;
            const files = Array.from(e.dataTransfer.files);
            if (files.length > 0) onFiles(files);
        },
    };

    return { isDragActive, dropProps };
};
